import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { RefreshCw, Clock, Calendar, Bug, CheckCircle, XCircle, Smartphone } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * This is the EXACT same date formatting logic from the mechanic app
 * /app/mechanic-app-native/src/utils/dateFormat.ts
 * 
 * We use this to test the formatting in the browser before building APKs
 */

// Parse ISO datetime string and extract date/time components WITHOUT timezone conversion
const parseISTDateTime = (dateStr) => {
  if (!dateStr) return null;
  
  // Match ISO format: 2026-03-22T11:00:00+05:30 or 2026-03-22T11:00:00
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (isoMatch) {
    return {
      year: parseInt(isoMatch[1]),
      month: parseInt(isoMatch[2]) - 1, // 0-indexed
      day: parseInt(isoMatch[3]),
      hours: parseInt(isoMatch[4]),
      minutes: parseInt(isoMatch[5]),
      seconds: parseInt(isoMatch[6])
    };
  }
  
  // Fallback to Date parsing for other formats
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  
  return {
    year: d.getFullYear(),
    month: d.getMonth(),
    day: d.getDate(),
    hours: d.getHours(),
    minutes: d.getMinutes(),
    seconds: d.getSeconds()
  };
};

// Format date to "dd MMM" (e.g., "22 Dec")
const formatDateShort = (date) => {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  if (typeof date === 'string') {
    const parsed = parseISTDateTime(date);
    if (!parsed) return '-';
    return `${parsed.day} ${months[parsed.month]}`;
  }
  
  const d = date;
  if (isNaN(d.getTime())) return '-';
  
  const day = d.getDate();
  const month = months[d.getMonth()];
  
  return `${day} ${month}`;
};

// Format time to "hh:mm AM/PM" (e.g., "02:30 PM")
const formatTime = (date) => {
  if (typeof date === 'string') {
    const parsed = parseISTDateTime(date);
    if (!parsed) return '-';
    
    let hours = parsed.hours;
    const minutes = parsed.minutes.toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    
    return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
  }
  
  const d = date;
  if (isNaN(d.getTime())) return '-';
  
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12;
  hours = hours ? hours : 12;
  
  return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
};

// OLD buggy implementation (for comparison)
const formatTimeOLD = (dateStr) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  
  let hours = d.getHours();
  const minutes = d.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  
  hours = hours % 12;
  hours = hours ? hours : 12;
  
  return `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
};

export default function MechanicAppTestPage() {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mechanicPhone, setMechanicPhone] = useState('8790910645');
  const [mechanicToken, setMechanicToken] = useState('');
  const [browserTimezone, setBrowserTimezone] = useState('');
  const [testResults, setTestResults] = useState([]);

  useEffect(() => {
    // Get browser timezone
    setBrowserTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  }, []);

  const loginAsMechanic = async () => {
    setLoading(true);
    try {
      // Request OTP
      await axios.post(`${API_URL}/auth/request-otp`, { phone: mechanicPhone });
      
      // Verify with dev OTP
      const response = await axios.post(`${API_URL}/auth/verify-otp`, {
        phone: mechanicPhone,
        otp: '123456'
      });
      
      if (response.data.token) {
        setMechanicToken(response.data.token);
        toast.success('Logged in as mechanic!');
        fetchInspections(response.data.token);
      } else {
        toast.error('Failed to get token');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const fetchInspections = async (token) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/mechanic/inspections`, {
        headers: { Authorization: `Bearer ${token || mechanicToken}` }
      });
      
      setInspections(response.data || []);
      
      // Run tests on the data
      runFormatTests(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch inspections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const runFormatTests = (data) => {
    const results = [];
    
    for (const insp of data.slice(0, 10)) {
      const raw = insp.scheduledAt;
      const newFormat = formatTime(raw);
      const oldFormat = formatTimeOLD(raw);
      const dateFormat = formatDateShort(raw);
      
      // Extract expected time from ISO string
      const match = raw?.match(/T(\d{2}):(\d{2})/);
      let expectedTime = 'N/A';
      if (match) {
        let h = parseInt(match[1]);
        const m = match[2];
        const ampm = h >= 12 ? 'PM' : 'AM';
        h = h % 12 || 12;
        expectedTime = `${h.toString().padStart(2, '0')}:${m} ${ampm}`;
      }
      
      results.push({
        vehicle: insp.vehicleNumber,
        raw: raw,
        expected: expectedTime,
        newFormat: newFormat,
        oldFormat: oldFormat,
        dateFormat: dateFormat,
        passed: newFormat === expectedTime
      });
    }
    
    setTestResults(results);
  };

  const manualTest = (isoString) => {
    const newFormat = formatTime(isoString);
    const oldFormat = formatTimeOLD(isoString);
    const dateFormat = formatDateShort(isoString);
    
    return { newFormat, oldFormat, dateFormat };
  };

  const [testInput, setTestInput] = useState('2026-03-22T11:00:00+05:30');
  const [manualResult, setManualResult] = useState(null);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Smartphone className="h-8 w-8 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Mechanic App - Time Format Test Page</h1>
            <p className="text-gray-500">Debug date/time formatting without building APKs</p>
          </div>
        </div>

        {/* Browser Info */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Bug className="h-5 w-5" />
              Debug Info
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Browser Timezone</div>
                <div className="font-mono font-medium">{browserTimezone}</div>
              </div>
              <div>
                <div className="text-gray-500">Current Time (Local)</div>
                <div className="font-mono font-medium">{new Date().toLocaleTimeString()}</div>
              </div>
              <div>
                <div className="text-gray-500">Current Time (UTC)</div>
                <div className="font-mono font-medium">{new Date().toUTCString()}</div>
              </div>
              <div>
                <div className="text-gray-500">Mechanic Token</div>
                <div className="font-mono font-medium">{mechanicToken ? '✅ Set' : '❌ Not set'}</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manual Test */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Manual Format Test</CardTitle>
            <CardDescription>Test any ISO datetime string</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="flex-1">
                <Label>ISO DateTime String</Label>
                <Input 
                  value={testInput}
                  onChange={(e) => setTestInput(e.target.value)}
                  placeholder="2026-03-22T11:00:00+05:30"
                  className="font-mono"
                />
              </div>
              <Button onClick={() => setManualResult(manualTest(testInput))}>
                Test Format
              </Button>
            </div>
            
            {manualResult && (
              <div className="mt-4 p-4 bg-gray-100 rounded-lg">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">NEW Format (Fixed)</div>
                    <div className="font-mono text-lg font-bold text-green-600">{manualResult.newFormat}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">OLD Format (Buggy)</div>
                    <div className="font-mono text-lg font-bold text-red-600">{manualResult.oldFormat}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Date Format</div>
                    <div className="font-mono text-lg font-bold">{manualResult.dateFormat}</div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Login & Fetch */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Fetch Real Mechanic Data</CardTitle>
            <CardDescription>Login as mechanic and fetch actual inspection data</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 items-end">
              <div className="w-48">
                <Label>Mechanic Phone</Label>
                <Input 
                  value={mechanicPhone}
                  onChange={(e) => setMechanicPhone(e.target.value)}
                  placeholder="8790910645"
                />
              </div>
              <Button onClick={loginAsMechanic} disabled={loading}>
                {loading ? 'Loading...' : 'Login & Fetch'}
              </Button>
              {mechanicToken && (
                <Button variant="outline" onClick={() => fetchInspections()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Test Results */}
        {testResults.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                Format Test Results
                <Badge variant={testResults.every(r => r.passed) ? "default" : "destructive"}>
                  {testResults.filter(r => r.passed).length}/{testResults.length} Passed
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-3">Vehicle</th>
                      <th className="text-left py-2 px-3">Raw scheduledAt</th>
                      <th className="text-left py-2 px-3">Expected</th>
                      <th className="text-left py-2 px-3">NEW Format</th>
                      <th className="text-left py-2 px-3">OLD Format</th>
                      <th className="text-left py-2 px-3">Date</th>
                      <th className="text-center py-2 px-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testResults.map((result, idx) => (
                      <tr key={idx} className={`border-b ${result.passed ? '' : 'bg-red-50'}`}>
                        <td className="py-2 px-3 font-medium">{result.vehicle}</td>
                        <td className="py-2 px-3 font-mono text-xs">{result.raw}</td>
                        <td className="py-2 px-3 font-mono text-blue-600">{result.expected}</td>
                        <td className="py-2 px-3 font-mono font-bold text-green-600">{result.newFormat}</td>
                        <td className="py-2 px-3 font-mono text-red-600">{result.oldFormat}</td>
                        <td className="py-2 px-3">{result.dateFormat}</td>
                        <td className="py-2 px-3 text-center">
                          {result.passed ? (
                            <CheckCircle className="h-5 w-5 text-green-500 inline" />
                          ) : (
                            <XCircle className="h-5 w-5 text-red-500 inline" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Raw Inspections Data */}
        {inspections.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Raw API Response ({inspections.length} inspections)</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-auto">
                <pre className="text-xs bg-gray-900 text-green-400 p-4 rounded-lg">
                  {JSON.stringify(inspections.slice(0, 5), null, 2)}
                </pre>
              </div>
            </CardContent>
          </Card>
        )}

        {/* How Mechanic App Should Display */}
        {inspections.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Mechanic App Preview (How it should look)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {inspections.slice(0, 6).map((insp, idx) => (
                  <div key={idx} className="bg-white border rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-bold text-lg">{insp.vehicleNumber}</div>
                        <div className="text-gray-500 text-sm">{insp.customerName}</div>
                      </div>
                      <Badge variant={insp.status === 'NEW' ? 'secondary' : insp.status === 'IN_PROGRESS' ? 'default' : 'outline'}>
                        {insp.status}
                      </Badge>
                    </div>
                    <div className="mt-3 flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span>{formatDateShort(insp.scheduledAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="font-medium text-green-600">{formatTime(insp.scheduledAt)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
